let SCRIPT_NONCE: string | null = null;

/**
 * Retrieves and caches the CSP nonce from script tags with data-csp-nonce attributes.
 * This attribute is set by our Cloudflare middleware.
 * @returns The CSP nonce string if found, otherwise null.
 */
export const getCSPNonce = (): string | null => {
	if (SCRIPT_NONCE) {
		return SCRIPT_NONCE;
	}

	const DEBUG_CSP_NONCE = window.location.search.includes("debugcsp");

	if (DEBUG_CSP_NONCE) {
		console.log("[CSP-Debug] Attempting to find nonce...");
	}

	try {
		// Look for any script tag with a data-csp-nonce attribute (set by our middleware)
		const scripts = Array.from(document.getElementsByTagName("script"));

		if (DEBUG_CSP_NONCE) {
			console.log(
				`[CSP-Debug] Found ${scripts.length} script tags to check for nonce.`,
			);
		}

		// First try with data-csp-nonce (works in all browsers)
		const noncedScript = scripts.find((script) => {
			const nonceVal = script.getAttribute("data-csp-nonce");
			return typeof nonceVal === "string" && nonceVal !== "";
		});

		if (noncedScript) {
			SCRIPT_NONCE = noncedScript.getAttribute("data-csp-nonce");
			if (DEBUG_CSP_NONCE) {
				console.log(
					`[CSP-Debug] SUCCESS: Found valid data-csp-nonce="${SCRIPT_NONCE}" on script tag.`,
				);
			}
		} else if (DEBUG_CSP_NONCE) {
			console.warn(
				"[CSP-Debug] WARNING: No script tag found with a data-csp-nonce attribute.",
			);
		}

		// Final check and logging if no nonce was found
		if (
			!SCRIPT_NONCE &&
			(DEBUG_CSP_NONCE || window.location.hostname !== "localhost")
		) {
			console.error(
				"[CSP] Critical: No valid nonce found. Content Security Policy may block inline styles.",
			);
		}
	} catch (e) {
		if (DEBUG_CSP_NONCE || window.location.hostname !== "localhost") {
			console.error("[CSP] Critical: Error during nonce retrieval process:", e);
		}
		SCRIPT_NONCE = null; // Ensure it's null on error
	}

	if (DEBUG_CSP_NONCE) {
		console.log(`[CSP-Debug] Final nonce value: "${SCRIPT_NONCE || "null"}"`);
	}
	return SCRIPT_NONCE;
};

/**
 * Sets up the necessary infrastructure to ensure all dynamically created style tags
 * get the proper CSP nonce automatically.
 */
if (typeof window !== "undefined" && typeof document !== "undefined") {
	// Apply nonce to any style tag that doesn't have one yet
	const applyNonceToStyleTag = (styleEl: Element) => {
		if (!SCRIPT_NONCE) return; // No nonce available

		if (!styleEl.hasAttribute("nonce") && styleEl.nodeName === "STYLE") {
			styleEl.setAttribute("nonce", SCRIPT_NONCE);

			const DEBUG_CSP_STYLE = window.location.search.includes("debugcsp");

			if (DEBUG_CSP_STYLE) {
				console.log("[CSP-Style] Added nonce to dynamically created style tag");
			}
		}
	};

	// DEFENSE 1: Monkey patch document.createElement to catch style tags at creation
	const setupDomPatches = () => {
		if (!SCRIPT_NONCE) return; // Can't do anything without a nonce

		const DEBUG_CSP_STYLE = window.location.search.includes("debugcsp");

		// Patch document.createElement to add nonce at element creation time
		const originalCreateElement = document.createElement;
		document.createElement = (
			tagName: string,
			options?: ElementCreationOptions,
		): HTMLElement => {
			const element = originalCreateElement.call(document, tagName, options);
			if (tagName.toLowerCase() === "style" && SCRIPT_NONCE) {
				element.setAttribute("nonce", SCRIPT_NONCE);
				if (DEBUG_CSP_STYLE) {
					console.log(
						"[CSP-Style] Tagged newly created style element with nonce",
					);
				}
			}
			return element;
		};

		// Patch document.head methods for direct style insertions
		if (document.head) {
			// TypeScript types for the DOM methods
			type AppendChildFn = <T extends Node>(node: T) => T;
			type InsertBeforeFn = <T extends Node>(node: T, child: Node | null) => T;

			// Store original methods
			const originalAppendChild = document.head.appendChild;
			const originalInsertBefore = document.head.insertBefore;

			// Override appendChild
			(document.head.appendChild as unknown as AppendChildFn) = function (
				this: typeof document.head,
				node: Node,
			) {
				if (node.nodeType === 1) {
					const elem = node as unknown as Element;
					if (
						elem.tagName === "STYLE" &&
						!elem.hasAttribute("nonce") &&
						SCRIPT_NONCE
					) {
						elem.setAttribute("nonce", SCRIPT_NONCE);
						if (DEBUG_CSP_STYLE) {
							console.log("[CSP-Style] Intercepted appendChild for style tag");
						}
					}
				}
				// biome-ignore lint/suspicious/noExplicitAny: DOM typings require this cast
				return originalAppendChild.call(this, node) as any;
			} as AppendChildFn;

			// Override insertBefore
			(document.head.insertBefore as unknown as InsertBeforeFn) = function (
				this: typeof document.head,
				node: Node,
				reference: Node | null,
			) {
				if (node.nodeType === 1) {
					const elem = node as unknown as Element;
					if (
						elem.tagName === "STYLE" &&
						!elem.hasAttribute("nonce") &&
						SCRIPT_NONCE
					) {
						elem.setAttribute("nonce", SCRIPT_NONCE);
						if (DEBUG_CSP_STYLE) {
							console.log("[CSP-Style] Intercepted insertBefore for style tag");
						}
					}
				}
				// biome-ignore lint/suspicious/noExplicitAny: DOM typings require this cast
				return originalInsertBefore.call(this, node, reference) as any;
			} as InsertBeforeFn;

			if (DEBUG_CSP_STYLE) {
				console.log("[CSP-Style] DOM method patching complete");
			}
		}

		// DEFENSE 2: Apply to existing style tags and set up MutationObserver for future tags
		// First, apply to any existing style tags that might have been missed
		const existingStyleTags = document.querySelectorAll("style:not([nonce])");
		for (const styleTag of existingStyleTags) {
			applyNonceToStyleTag(styleTag);
		}

		// Watch for future style tags being added (backup defense)
		const observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.type === "childList") {
					for (const node of mutation.addedNodes) {
						if (node.nodeType === 1) {
							const element = node as Element;
							if (element.nodeName === "STYLE") {
								applyNonceToStyleTag(element);
							}
							if ("querySelectorAll" in element) {
								const childStyleTags =
									element.querySelectorAll("style:not([nonce])");
								for (const styleTag of childStyleTags) {
									applyNonceToStyleTag(styleTag);
								}
							}
						}
					}
				}
			}
		});

		observer.observe(document.documentElement, {
			childList: true,
			subtree: true,
		});

		if (DEBUG_CSP_STYLE) {
			console.log("[CSP-Style] CSP protection system active");
		}
	};

	// Initialize the nonce when this module is first imported
	getCSPNonce();

	// Set up DOM patches once we have a nonce
	if (SCRIPT_NONCE) {
		setupDomPatches();
	} else {
		// If no nonce available yet, wait for DOMContentLoaded and try again
		document.addEventListener("DOMContentLoaded", () => {
			if (!SCRIPT_NONCE) {
				getCSPNonce(); // Try one more time
			}
			if (SCRIPT_NONCE) {
				setupDomPatches();
			}
		});
	}
}

/**
 * The cached CSP nonce value.
 * Import this value in other modules that need the nonce.
 */
export { SCRIPT_NONCE };
